/** The current layout, recomputed when the viewport changes.
 *
 *  Reads width *and* height, because orientation decides between two days and
 *  two lanes in the 720–1023 band. Rotating an iPad therefore changes the
 *  layout without changing the width, which a width-only media query could not
 *  express — one of two reasons this is not CSS. The other is R12's measured
 *  fallback, which needs to know what the day actually holds.
 */

import { useEffect, useState } from 'preact/hooks';
import { layoutFor, type Layout } from './breakpoints';

export interface Viewport {
  width: number;
  height: number;
  layout: Layout;
}

const read = (): Viewport => {
  const width = typeof window === 'undefined' ? 390 : window.innerWidth;
  const height = typeof window === 'undefined' ? 844 : window.innerHeight;
  return { width, height, layout: layoutFor(width, height) };
};

export function useLayout(): Viewport {
  const [viewport, setViewport] = useState<Viewport>(read);

  useEffect(() => {
    // `resize` covers rotation, Split View and Stage Manager alike — all three
    // arrive as a resize, which is why iPad needs no separate handling.
    const measure = () => setViewport(read());
    addEventListener('resize', measure);
    addEventListener('orientationchange', measure);
    return () => {
      removeEventListener('resize', measure);
      removeEventListener('orientationchange', measure);
    };
  }, []);

  return viewport;
}
